import { UserButton, useClerk } from '@clerk/react'
import { dark } from '@clerk/ui/themes'
import {
  ClipboardCheck,
  Code,
  Github,
  HelpCircle,
  Keyboard,
  LayoutGrid,
  Timer,
} from 'lucide-react'
import { useEffect } from 'react'
import { openUserProfileTab } from '@/lib/clerk-navigation'
import { settingsSectionLabel } from './settings/sections'
import { useTheme } from './theme-provider'
import {
  CodeEditorSettings,
  GitHubIntegrationSettings,
  HelpSettings,
  LayoutsSettings,
  PostmortemSettings,
  ShortcutsSettings,
  TimerSettings,
} from './user-profile'
import { ClerkSettingsPage } from './user-profile/clerk-settings-page'

export function ThemedUserButton() {
  const { resolvedTheme } = useTheme()
  const { closeUserProfile } = useClerk()

  // After the GitHub OAuth callback redirects to /?github_sync_pending=1 (or
  // an error), open the GitHub Integration profile page so the user can finish
  // owner/repo selection. The settings panel itself shows the toast and
  // strips the params once it mounts. Clerk hydrates the UserButton
  // asynchronously, so poll for the trigger before opening (bounded at 10s).
  //
  // Deliberately not on useOpenSettingsSection: the decided destination is
  // /settings/github-integration, but the completion UI (owner/repo pick, toast,
  // ?github_sync_* cleanup) is not ported there yet. At cutover (LG-1086) the
  // OAuth redirect moves to /settings/github-integration?... and this effect
  // is deleted with the modal.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (
      params.get('github_sync_pending') !== '1' &&
      !params.get('github_sync_error')
    ) {
      return
    }
    const deadline = Date.now() + 10_000
    let timeoutId: ReturnType<typeof setTimeout> | undefined
    function openWhenReady(): void {
      const trigger = document.querySelector('[data-clerk-user-button] button')
      if (trigger) {
        openUserProfileTab(settingsSectionLabel('github-integration'))
      } else if (Date.now() < deadline) {
        timeoutId = setTimeout(openWhenReady, 200)
      }
    }
    openWhenReady()
    return () => {
      if (timeoutId !== undefined) clearTimeout(timeoutId)
    }
  }, [])
  const isDark = resolvedTheme === 'dark'

  // IMPORTANT: data-clerk-user-button is used by lib/clerk-navigation.ts
  // to programmatically open the UserButton dropdown. Do NOT rename or remove
  // this attribute without updating openUserProfileTab().
  return (
    <div data-clerk-user-button>
      <UserButton
        appearance={{
          theme: isDark ? dark : undefined,
          // These classes are used by lib/clerk-navigation.ts to target
          // specific Clerk elements without iterating all buttons on the page.
          // The element descriptor keys are part of Clerk's public typed API
          // (@clerk/shared/types ElementsConfig) — more stable than .cl-* classes.
          elements: {
            userButtonPopoverActionButton__manageAccount:
              'js-clerk-manage-account',
            navbarButton: 'js-clerk-nav-btn',
          },
        }}
        userProfileProps={{
          appearance: {
            theme: isDark ? dark : undefined,
            elements: {
              // Clerk's profile modal width is controlled by its modal wrapper
              // plus card container. Set both so desktop can expand while still
              // shrinking on narrow viewports.
              card: 'w-full max-w-none',
              cardBox:
                '!w-[95vw] lg:!w-[66vw] lg:!max-w-[1100px] !max-w-none sm:rounded-xl',
              navbarButton: 'js-clerk-nav-btn',
              rootBox: '!w-[95vw] lg:!w-[66vw] lg:!max-w-[1100px] !max-w-none',
            },
          },
        }}
      >
        {/* Tab labels come from the settings registry: the deep-link
            fallback (useOpenSettingsSection) finds a tab by its label, so the
            two must never drift apart. */}
        <UserButton.UserProfilePage
          label={settingsSectionLabel('code-editor')}
          labelIcon={<Code className="h-4 w-4" />}
          url="code-editor"
        >
          <ClerkSettingsPage title={settingsSectionLabel('code-editor')}>
            <CodeEditorSettings />
          </ClerkSettingsPage>
        </UserButton.UserProfilePage>
        <UserButton.UserProfilePage
          label={settingsSectionLabel('timer')}
          labelIcon={<Timer className="h-4 w-4" />}
          url="timer"
        >
          <ClerkSettingsPage title={settingsSectionLabel('timer')}>
            <TimerSettings />
          </ClerkSettingsPage>
        </UserButton.UserProfilePage>
        <UserButton.UserProfilePage
          label={settingsSectionLabel('shortcuts')}
          labelIcon={<Keyboard className="h-4 w-4" />}
          url="shortcuts"
        >
          <ClerkSettingsPage title={settingsSectionLabel('shortcuts')}>
            <ShortcutsSettings />
          </ClerkSettingsPage>
        </UserButton.UserProfilePage>
        <UserButton.UserProfilePage
          label={settingsSectionLabel('postmortem')}
          labelIcon={<ClipboardCheck className="h-4 w-4" />}
          url="postmortem"
        >
          <ClerkSettingsPage
            title={settingsSectionLabel('postmortem')}
            description="Configure your post-session reflection experience"
          >
            <PostmortemSettings />
          </ClerkSettingsPage>
        </UserButton.UserProfilePage>
        <UserButton.UserProfilePage
          label={settingsSectionLabel('help')}
          labelIcon={<HelpCircle className="h-4 w-4" />}
          url="help"
        >
          <HelpSettings />
        </UserButton.UserProfilePage>
        <UserButton.UserProfilePage
          label={settingsSectionLabel('layouts')}
          labelIcon={<LayoutGrid className="h-4 w-4" />}
          url="layouts"
        >
          <ClerkSettingsPage title={settingsSectionLabel('layouts')}>
            <LayoutsSettings
              onDone={() => closeUserProfile()}
              aboveClerkModal
            />
          </ClerkSettingsPage>
        </UserButton.UserProfilePage>
        <UserButton.UserProfilePage
          label={settingsSectionLabel('github-integration')}
          labelIcon={<Github className="h-4 w-4" />}
          url="github-integration"
        >
          <GitHubIntegrationSettings />
        </UserButton.UserProfilePage>
      </UserButton>
    </div>
  )
}
